export const privacyPolicy = {
  slug: "privacy-policy",
  titleStart: "Privacy",
  titleAccent: "Policy.",
  updated: "Last updated: July 2026",
  intro:
    "This policy explains what information IT Device Services collects, why we collect it and how we handle it. We keep things simple: we collect as little as possible and we never sell your data.",
  sections: [
    {
      heading: "Who We Are",
      paragraphs: [
        "IT Device Services provides device management, hardware lifecycle and security services to businesses. For anything related to this policy you can reach us at info@itdeviceservices.com or +1 (682) 231-1534.",
      ],
    },
    {
      heading: "Information We Collect",
      paragraphs: [
        "We collect information you choose to give us and a small amount of technical information that keeps the site working properly.",
      ],
      bullets: [
        "Contact details you submit through our contact form: name, email address, company, phone number and your message.",
        "Booking details you provide when scheduling a call through our scheduling provider.",
        "Basic technical data such as browser type and pages visited, used only to keep the site reliable.",
      ],
    },
    {
      heading: "How We Use Your Information",
      paragraphs: [
        "We use the information you share to respond to your enquiries, schedule and prepare for discovery calls, deliver our services and improve the website. We do not use your information for automated decision making and we do not sell it to anyone.",
      ],
    },
    {
      heading: "Sharing",
      paragraphs: [
        "We only share information with service providers that help us run the business, such as our scheduling provider and our hosting provider. These providers may only use your information to deliver their service to us. We may also disclose information if the law requires it.",
      ],
    },
    {
      heading: "Data Retention",
      paragraphs: [
        "We keep contact form submissions for as long as needed to handle your enquiry and maintain our business records. If you would like your information removed, email us and we will delete it unless we are required to keep it.",
      ],
    },
    {
      heading: "Security",
      paragraphs: [
        "We protect your information with appropriate technical and organizational measures, including encrypted connections and restricted access. No method of transmission or storage is completely secure, but we work to keep risk to a minimum.",
      ],
    },
    {
      heading: "Your Rights",
      paragraphs: [
        "Depending on where you live, you may have the right to access, correct or delete the personal information we hold about you, and to object to or restrict certain processing. To exercise any of these rights, contact us at info@itdeviceservices.com.",
      ],
    },
    {
      heading: "Third Party Services",
      paragraphs: [
        "Bookings made through the Get in Touch buttons are handled by Calendly, which has its own privacy policy. Links to social networks on this site lead to platforms we do not control. We recommend reviewing their policies before using them.",
      ],
    },
    {
      heading: "Changes to This Policy",
      paragraphs: [
        "If we update this policy we will post the new version on this page and adjust the date above. Significant changes will be highlighted on the site.",
      ],
    },
  ],
};

export const termsOfService = {
  slug: "terms-of-service",
  titleStart: "Terms of",
  titleAccent: "Service.",
  updated: "Last updated: July 2026",
  intro:
    "These terms govern your use of the IT Device Services website. By using the site you agree to them, so please take a moment to read through.",
  sections: [
    {
      heading: "Agreement to Terms",
      paragraphs: [
        "By accessing this website you agree to be bound by these terms and by our Privacy Policy. If you do not agree, please do not use the site. Service engagements are governed by separate written agreements between IT Device Services and each client.",
      ],
    },
    {
      heading: "Our Services",
      paragraphs: [
        "The website describes our device management, hardware lifecycle and security offerings. Content on this site is provided for general information and does not constitute a binding offer. Scope, pricing and timelines are agreed individually with each client.",
      ],
    },
    {
      heading: "Use of the Website",
      paragraphs: [
        "You agree to use the site lawfully and not to interfere with its operation, attempt to gain unauthorized access to any systems, or submit false or misleading information through our forms.",
      ],
    },
    {
      heading: "Intellectual Property",
      paragraphs: [
        "All content on this site, including text, graphics, logos and page design, belongs to IT Device Services or its licensors. You may not reproduce or redistribute it without our written permission, except for personal, non-commercial viewing.",
      ],
    },
    {
      heading: "Disclaimers",
      paragraphs: [
        "The website is provided on an as-is and as-available basis. While we work to keep information accurate and the site available, we make no warranties of any kind, express or implied, about completeness, accuracy or availability.",
      ],
    },
    {
      heading: "Limitation of Liability",
      paragraphs: [
        "To the fullest extent permitted by law, IT Device Services will not be liable for indirect, incidental or consequential damages arising from your use of this website. Nothing in these terms limits liability that cannot be limited by law.",
      ],
    },
    {
      heading: "Third Party Links",
      paragraphs: [
        "The site contains links to third party services such as our scheduling provider and social platforms. We are not responsible for their content or practices.",
      ],
    },
    {
      heading: "Changes to These Terms",
      paragraphs: [
        "We may update these terms from time to time. The current version will always be available on this page with the date above. Continued use of the site after changes means you accept the updated terms.",
      ],
    },
    {
      heading: "Contact",
      paragraphs: [
        "Questions about these terms can be sent to info@itdeviceservices.com or +1 (682) 231-1534.",
      ],
    },
  ],
};

export const cookiePolicy = {
  slug: "cookie-policy",
  titleStart: "Cookie",
  titleAccent: "Policy.",
  updated: "Last updated: July 2026",
  intro:
    "This page explains what cookies are, which ones this site uses and how you can control them.",
  sections: [
    {
      heading: "What Are Cookies",
      paragraphs: [
        "Cookies are small text files stored on your device by your browser. They help websites remember your choices and understand how pages are used. Similar technologies, such as local storage, work in much the same way and are covered by this policy.",
      ],
    },
    {
      heading: "How We Use Cookies",
      paragraphs: [
        "We keep cookie use to a minimum. This site does not run advertising or cross-site tracking cookies.",
      ],
      bullets: [
        "Essential: required for the site to function correctly, such as remembering your cookie consent choice.",
        "Third party: our scheduling provider, Calendly, may set cookies when you open the booking window. These are controlled by Calendly under its own cookie policy.",
      ],
    },
    {
      heading: "Your Consent",
      paragraphs: [
        "When you first visit the site, a banner asks whether you accept cookies. Your choice is stored locally on your device so we do not have to ask again. You can change your mind at any time by clearing your browser data for this site, after which the banner will appear again.",
      ],
    },
    {
      heading: "Managing Cookies",
      paragraphs: [
        "Most browsers let you view, block and delete cookies through their settings. Blocking essential cookies may affect how parts of the site work, such as the booking window.",
      ],
    },
    {
      heading: "Updates to This Policy",
      paragraphs: [
        "If our cookie use changes we will update this page and adjust the date above.",
      ],
    },
    {
      heading: "Contact",
      paragraphs: [
        "Questions about cookies on this site can be sent to info@itdeviceservices.com.",
      ],
    },
  ],
};
